import type { VariableType } from "@/workflow_api/hooks/variables/types";
import type {
  DataCenterSource,
  DCConditionData,
  ScopeState,
  ValueInputType,
  ValueColumn,
} from "./types";
import type { FilterDataType, Filter } from "@/workflow_api/types/core";
import {
  DataCenterConditionOperations,
  type DataCenterTypeOperationTypes,
} from "./components/condition-operations";
import { isValidDateString } from "@/utils/dates";
import type { Variable } from "../types";

export const INIT_SCOPE_STATE: ScopeState = {
  comparativeValue: "",
  operationType: "input",
  scope: {
    id: "created_at",
    name: "Fecha de creación",
    type: "dateScope",
    data_type: "date",
  },
  conditionScope: { name: "ANY", label: "Cualquiera" },
  initialRange: { value: "", type: "input" },
  endRange: { value: "", type: "input" },
  dateRangeError: "",
};

/**
 * Order of data center sources
 * When paginating or deserializing, sources are fetched in this order
 */
export const SOURCE_ORDER: readonly DataCenterSource[] = [
  "sdt",
  "Orquestador",
  "Operador",
] as const;

/**
 * Get the next source in the sequence
 * @param current - The current source
 * @returns The next source, or null if current is the last one
 *
 * @example
 * getNextSource("sdt") // returns "Orquestador"
 * getNextSource("Operador") // returns null
 */
export function getNextSource(
  current: DataCenterSource,
): DataCenterSource | null {
  const currentIndex = SOURCE_ORDER.indexOf(current);

  // Source not found or is the last one
  if (currentIndex === -1 || currentIndex === SOURCE_ORDER.length - 1) {
    return null;
  }

  return SOURCE_ORDER[currentIndex + 1];
}

/**
 * Check if a source is the last one in the order
 * @param source - The source to check
 * @returns True if this is the last source
 */
export function isLastSource(source: DataCenterSource): boolean {
  return SOURCE_ORDER.indexOf(source) === SOURCE_ORDER.length - 1;
}

/**
 * Get remaining sources after the current one
 * @param current - The current source
 * @returns Array of sources that come after current
 *
 * @example
 * getRemainingSources("sdt") // returns ["Orquestador", "Operador"]
 * getRemainingSources("Orquestador") // returns ["Operador"]
 * getRemainingSources("Operador") // returns []
 */
export function getRemainingSources(
  current: DataCenterSource,
): DataCenterSource[] {
  const currentIndex = SOURCE_ORDER.indexOf(current);

  if (currentIndex === -1 || currentIndex === SOURCE_ORDER.length - 1) {
    return [];
  }

  return SOURCE_ORDER.slice(currentIndex + 1);
}

/**
 * Create an empty conditon element
 */
export const createEmptyCondition = (): DCConditionData => ({
  id: Date.now().toString(),
  variable: null,
  operation: null,
  comparativeValue: "",
  initialRangeValue: "",
  endRangeValue: "",
  logicalOperator: "and",
  operationValueType: "input",
  initialRangeValueType: "input",
  endRangeValueType: "input",
});

/**
 * Get the input placeholder by the data type
 *
 * @param type data type
 * @returns
 */
export const getPlaceholderForType = (type: string): string => {
  switch (type) {
    case "string":
      return "Escribe un texto";
    case "number":
      return "Escribe un número";
    case "date":
      return "Selecciona una fecha";
    case "boolean":
      return "Elige verdadero o falso";
    case "variable":
      return "Elige una variable";
    case "regex":
      return "Escribe una expresión regular";
    case "positive-integer":
      return "Escribe un número entero positivo";
    default:
      return "Ingresa un valor";
  }
};

/**
 * Maps ValueInputType to string list for filtering variables by type
 */
export const mapValueInputTypeToVariableType = (
  valueInputType: ValueInputType,
): string[] => {
  switch (valueInputType) {
    case "string":
    case "regex":
      return ["string", "email"];
    case "number":
    case "positive-integer":
      return ["number"];
    case "date":
      return ["date"];
    case "boolean":
      return ["boolean"];
    default:
      return []; // fallback to show all for generic variable type
  }
};

/**
 * Maps VariableType to ValueInputType, with fallback for unsupported types
 */
export const mapVariableTypeToValueInputType = (
  variableType: VariableType | undefined,
): ValueInputType => {
  switch (variableType) {
    case "string":
      return "string";
    case "number":
      return "number";
    case "float":
      return "number"; // Map float to number
    case "date":
      return "date";
    case "boolean":
      return "boolean";
    case "array":
    case "file":
    case "select":
    case "":
    default:
      return "string"; // fallback for unsupported types
  }
};

/**
 * Maps database column types to ValueInputType
 */
export const mapColumnTypeToValueInputType = (
  columnType: string,
): ValueInputType => {
  switch (columnType.toLowerCase()) {
    case "string":
    case "text":
    case "varchar":
    case "char":
      return "string";
    case "number":
    case "int":
    case "integer":
    case "float":
    case "double":
    case "decimal":
      return "number";
    case "date":
    case "datetime":
    case "timestamp":
      return "date";
    case "boolean":
    case "bool":
      return "boolean";
    case "regex":
      return "regex";
    case "positive-integer":
      return "positive-integer";
    default:
      return "string"; // fallback to string for unknown types
  }
};

/**
 * Parse backend type in uppercase to app supported types
 * Used in deserialization and table details column type parsing
 * @param type
 * @returns
 */
export const parseType = (type: string | undefined) => {
  if (!type) return "string";
  switch (type.toUpperCase()) {
    case "STRING":
      return "string";
    case "FLOAT":
    case "INTEGER":
    case "NUMBER":
      return "number";
    case "DECIMAL":
      return "number";
    case "BOOLEAN":
      return "boolean";
    case "DATE":
      return "date";
    case "ARRAY":
      return "array";
    default:
      return "string";
  }
};

/**
 * Check and parse value according to type
 * If parsing fails, returns empty string
 * Used in deserialization
 *
 * @param value
 * @param type
 * @returns
 */
export const tryParseValueType = (
  value: string,
  type: ValueInputType,
): string => {
  switch (type) {
    case "number": {
      try {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? "" : value;
      } catch {
        return "";
      }
    }
    case "boolean": {
      if (typeof value === "boolean") return String(value);
      const lowered = typeof value === "string" ? value.toLowerCase() : "";
      return lowered === "true" || lowered === "false" ? value : "";
    }
    case "date":
      return isValidDateString(String(value)) ? value : "";
    case "string":
      return value;
    default:
      return value;
  }
};

export const getFilterType = (
  operationType?: ValueInputType | VariableType,
): FilterDataType => {
  switch (operationType) {
    case "positive-integer":
    case "number":
      return "NUMBER";
    case "boolean":
      return "BOOLEAN";
    case "date":
      return "DATE";
    case "string":
    case "regex":
    default:
      return "STRING";
  }
};

export const getFilterValue = (
  value: string | null,
  type: FilterDataType,
): string | number | boolean | null => {
  if (value === null) return null;
  if (type === "NUMBER") {
    return Number(value);
  } else if (type === "BOOLEAN") {
    return value === "true";
  } else {
    return value;
  }
};

/**
 * Find a data center operation by the name
 * @param type operation name
 * @param dataType operation data type
 * @returns The operation object or undefined
 */
export const findOperation = (
  type: string,
  dataType: DataCenterTypeOperationTypes,
) => {
  const operations = DataCenterConditionOperations[dataType];
  if (!operations) return undefined;
  return operations.find((op) => op.name === type);
};

/**
 * Check if a string is a UUID (with optional suffix after an underscore)
 * Used to identify variable references in serialized column values
 * @param val1
 * @returns
 */
const isuuid = (val1: string) => {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(_.+)?$/i;
  return uuidRegex.test(val1);
};

/**
 * Extract range values from a filter condition for BETWEEN operations
 * Returns [startValue, endValue] parsed from JSON or array
 *
 * @param condition
 * @returns tuple of string values or null
 */
function deserializeRangeValue(val: string): Variable | string {
  if (isValidDateString(val)) return String(val);
  if (isuuid(val)) return { id: val, node_id: "" } as Variable;
  if (typeof val !== "object") return val;
  return "";
}

export const extractRangeValues = (
  node: Filter,
): [Variable | string, Variable | string] | null => {
  if (Array.isArray(node.value) && node.value.length === 2) {
    const [val1, val2] = node.value;
    if (isValidDateString(val1) && isValidDateString(val2)) {
      return [String(val1), String(val2)];
    }
  } else if (typeof node.value === "string") {
    try {
      const parsed = JSON.parse(node.value);
      if (Array.isArray(parsed) && parsed.length === 2) {
        return [deserializeRangeValue(parsed[0]), deserializeRangeValue(parsed[1])];
      }
    } catch {
      // not valid JSON
    }
  }
  return null;
};

/**
 * Get enriched columns from columnsDef, merged with values from columns
 * Uses label as the unique identifier key
 * Parses column values according to their type
 * @param columns - The existing columns with values (used for lookup by label)
 * @param columnsDef - The column definitions (Variable[])
 * @returns Enriched columns array using columnsDef structure with columns values
 */
export const enrichColumns = (
  columns: ValueColumn[],
  columnsDef: Variable[],
): ValueColumn[] => {
  return columnsDef.map((columnDef) => {
    // Look for existing column data by label (the key identifier)
    const existingColumn = columns.find((col) => col.label === columnDef.name);

    const valueType = mapVariableTypeToValueInputType(columnDef.type);

    // Parse the value according to its type
    const existingInputValue =
      typeof existingColumn?.value === "string" ? existingColumn.value : "";
    const parsedValue =
      existingColumn?.inputType === "input"
        ? tryParseValueType(existingInputValue, valueType)
        : (existingColumn?.value ?? "");

    return {
      name: columnDef.id, // Store the columnDef ID for reference
      label: columnDef.name, // Use label as the unique identifier key
      value: parsedValue,
      inputType: existingColumn?.inputType ?? "input",
      valueType,
      placeholder: getPlaceholderForType(columnDef.type),
      isRequired: false,
    };
  });
};

/**
 * Helper to update or create a column by label
 * Centralizes the logic for finding columnDef, updating existing or creating new
 * @param columnLabel - The column label (unique identifier)
 * @param updates - Partial updates to apply to the column
 * @param columns - Current columns array
 * @param columnsDef - Column definitions
 * @param enrichedColumns - Enriched columns (for fallback data)
 * @param setColumns - State setter function
 */
export const updateOrCreateColumn = (
  columnLabel: string,
  updates: Partial<ValueColumn>,
  columns: ValueColumn[],
  columnsDef: Variable[],
  enrichedColumns: ValueColumn[],
  setColumns: (cols: ValueColumn[]) => void,
) => {
  const columnDef = columnsDef.find((def) => def.name === columnLabel);
  if (!columnDef) return;

  const existingIndex = columns.findIndex((col) => col.label === columnLabel);

  if (existingIndex === -1) {
    // Create new column
    const enriched = enrichedColumns.find((col) => col.label === columnLabel);
    if (enriched) {
      setColumns([...columns, { ...enriched, name: columnDef.id, ...updates }]);
    }
  } else {
    // Update existing column
    const updated = [...columns];
    updated[existingIndex] = { ...updated[existingIndex], ...updates };
    setColumns(updated);
  }
};
